import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import { api } from '../services/api';

interface Row {
  state: string;
  quarter: number;
  order_count: number;
  taxable_amount: string;
  tax_collected: string;
}

const QUARTERS = [1, 2, 3, 4];

export default function TaxCollectedReportPage() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.tax.reportByState(year);
      setRows(data as Row[]);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  // Pivot to state × quarter grid
  const states = useMemo(() => Array.from(new Set(rows.map(r => r.state))).sort(), [rows]);
  const cellMap = useMemo(() => {
    const m = new Map<string, Row>();
    rows.forEach(r => m.set(`${r.state}-${r.quarter}`, r));
    return m;
  }, [rows]);

  const stateAnnualTotals = useMemo(() => {
    const m = new Map<string, { taxable: number; tax: number; orders: number }>();
    rows.forEach(r => {
      const cur = m.get(r.state) || { taxable: 0, tax: 0, orders: 0 };
      cur.taxable += parseFloat(r.taxable_amount) || 0;
      cur.tax += parseFloat(r.tax_collected) || 0;
      cur.orders += Number(r.order_count) || 0;
      m.set(r.state, cur);
    });
    return m;
  }, [rows]);

  const quarterTotals = useMemo(() => {
    const t: Record<number, { taxable: number; tax: number; orders: number }> = {
      1: { taxable: 0, tax: 0, orders: 0 },
      2: { taxable: 0, tax: 0, orders: 0 },
      3: { taxable: 0, tax: 0, orders: 0 },
      4: { taxable: 0, tax: 0, orders: 0 },
    };
    rows.forEach(r => {
      t[r.quarter].taxable += parseFloat(r.taxable_amount) || 0;
      t[r.quarter].tax += parseFloat(r.tax_collected) || 0;
      t[r.quarter].orders += Number(r.order_count) || 0;
    });
    return t;
  }, [rows]);

  const grandTotal = useMemo(() => {
    let taxable = 0, tax = 0, orders = 0;
    rows.forEach(r => {
      taxable += parseFloat(r.taxable_amount) || 0;
      tax += parseFloat(r.tax_collected) || 0;
      orders += Number(r.order_count) || 0;
    });
    return { taxable, tax, orders };
  }, [rows]);

  const downloadCsv = () => {
    const header = ['State', 'Quarter', 'Orders', 'Taxable Amount', 'Tax Collected'];
    const data = rows.map(r => [
      r.state,
      `Q${r.quarter}`,
      r.order_count,
      parseFloat(r.taxable_amount).toFixed(2),
      parseFloat(r.tax_collected).toFixed(2),
    ]);
    const csv = [header, ...data].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `tax-collected-${year}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tax Collected by State"
        description="Sales tax collected by state and quarter — for filing with each state's revenue department."
      />

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">{error}</div>}

      <Card>
        <div className="flex items-center gap-3 mb-4">
          <label className="text-sm">Year:</label>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="px-3 py-1 border rounded"
          >
            {[currentYear, currentYear - 1, currentYear - 2, currentYear - 3].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={downloadCsv} className="ml-auto px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
            Export CSV
          </button>
        </div>

        {loading ? <LoadingSpinner /> : states.length === 0 ? (
          <div className="text-center py-10 text-gray-500">
            No tax collected in {year}.
            <br />
            <span className="text-xs">If you've enabled a nexus state, tax will start appearing once orders ship to it.</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-300">
                  <th className="px-3 py-2">State</th>
                  {QUARTERS.map(q => (
                    <th key={q} className="px-3 py-2 text-right">Q{q}<br /><span className="text-gray-400 normal-case font-normal">tax / taxable / orders</span></th>
                  ))}
                  <th className="px-3 py-2 text-right bg-gray-50">Year total</th>
                </tr>
              </thead>
              <tbody>
                {states.map(state => {
                  const annual = stateAnnualTotals.get(state)!;
                  return (
                    <tr key={state} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono font-semibold">{state}</td>
                      {QUARTERS.map(q => {
                        const cell = cellMap.get(`${state}-${q}`);
                        if (!cell) return <td key={q} className="px-3 py-2 text-right text-gray-300">—</td>;
                        return (
                          <td key={q} className="px-3 py-2 text-right font-mono text-xs">
                            <div className="font-semibold text-gray-900">${parseFloat(cell.tax_collected).toFixed(2)}</div>
                            <div className="text-gray-500">${parseFloat(cell.taxable_amount).toLocaleString(undefined, { maximumFractionDigits: 0 })} · {cell.order_count}o</div>
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-right font-mono bg-gray-50">
                        <div className="font-bold text-gray-900">${annual.tax.toFixed(2)}</div>
                        <div className="text-xs text-gray-500">${annual.taxable.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-gray-100 font-semibold">
                  <td className="px-3 py-2">All states</td>
                  {QUARTERS.map(q => {
                    const t = quarterTotals[q];
                    return (
                      <td key={q} className="px-3 py-2 text-right font-mono text-xs">
                        <div>${t.tax.toFixed(2)}</div>
                        <div className="text-gray-500 font-normal">${t.taxable.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right font-mono bg-gray-200">
                    <div>${grandTotal.tax.toFixed(2)}</div>
                    <div className="text-xs text-gray-600 font-normal">${grandTotal.taxable.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
